// THE INVOICE EDITOR'S SAVE, AS DATA: the state the editor seeds from the
// stored invoice and lines, and the one function that turns that state into the
// body of `POST /invoices/{id}/intake-save`.
//
// Split out of `EditableInvoiceForm.tsx` (2026-09-25, `docs/decisions.md` D-181)
// so a test can build the editor's REAL payload rather than a hand-written copy
// of it. A hand-written body is how D-179 shipped refusing the app's own Import
// Control save: the test sent each line's stored total, and the editor sends a
// total it derives from price x quantity (D-4), which is not always the stored
// one. The form seeds its state with `headerStateFrom` / `lineItemStateFrom` and
// saves with `buildIntakeSaveBody`, and so do the tests.
//
// ONE BODY, AT THE ONE EDITING STEP (owner, 2026-09-29 — `docs/decisions.md`
// D-216). Import Control carries the whole header and every changed line. From
// D-174 until the merge it sent the Company and the D-128 defaults alone, while
// AP/AR Approval sent this full body; that second shape is gone with the
// Company-only rule. A line's department is sent only when the reviewer picked
// it this session (D-217) — see `departmentPicked`.

import { CHARGE_GROUP_NAME, CHARGE_KIND, isGroupRow, realLines, type ChargeKind } from '@ap/shared'
import type { Invoice, InvoiceLineItem, InvoiceLineItemWrite, IntakeSaveRequest } from './types'
import type { LineGroupOperation, RouteOverrideWrite } from '../api/types'
import { lineTotal, money2, priceFromWire, priceText, quantityWire, sameDecimal } from './format'
import { lineWriteSignature } from './saveOnBlur'

export interface HeaderState {
  externalinvoiceid: string
  /** The default the box was seeded with, when intake stored no number (D-128):
      the invoice date as loaded. The server owns that default, so the box is
      sent only when the reviewer typed something else (code review, 2026-10-03). */
  externalInvoiceIdDefault?: string
  date: string
  /** DERIVED and LOCKED (owner, 2026-08-14 — `docs/decisions.md` D-3): recomputed
      from the rows on every render by `derivedSubtotal`, never typed. Kept in this
      state object anyway so the reconcile check, the field and the save payload
      all read one value. */
  subtotal: string
  tax: string
  /** The second header charge (D-1). Editable, exactly like `tax`. */
  shipping: string
  total: string
  /** NO LONGER RENDERED (owner, 2026-08-14 — D-8): the control is gone from the
      modal because a non-USD invoice is not something anyone sets here. Loaded,
      and NOT sent: an absent field leaves the stored value alone (code review
      D-07, 2026-10-03). `NonUSDCurrency` still raises and still resolves from
      the Flags panel like every other explicit flag. */
  currency: string
  vendorId?: string
  vendorName?: string
  companyId?: string
  companyName?: string
  /** The reviewer's REQUEST for manual Point Click Care entry
      (`manual_entry_requested`), as loaded. Read for the release banner; NOT
      sent — this form has no control for it any more, so sending it only
      re-sent the value it opened with (code review D-07, 2026-10-03). */
  manualEntry: boolean
}

export interface LineItemState {
  lineItemId: string
  /** The row's key on screen, kept when a line added this session takes its
      stored id, so the row is not remounted under the reviewer's caret. Absent
      on a loaded line, whose key is its id (`rowKey`). */
  clientKey?: string
  /** Concurrency counter of the stored line; absent on a line not saved yet. */
  rowVersion?: number
  sequence?: number | null
  description: string
  quantity: string
  price: string
  /** LOCKED (D-4). Always `rowTotal(row, price, quantity)`: the server's
      stored Total while Price and Quantity equal the stored ones by value,
      else `lineTotal(price, quantity)`, the server's own rule (code review
      D-02). Never sent. */
  total: string
  /** The stored line as loaded: its Price, Quantity and Total. Absent on a
      line added this session, whose Total is always worked out. */
  stored?: { price: string; quantity: string; total: string }
  /** "Account" — the trailing half. */
  accountId?: string
  accountLabel?: string
  /** "Department" — the leading half, as stored (a derived one included). Optional
      at Import Control, the one field there that is not required (D-217). */
  departmentId?: string
  departmentLabel?: string
  /** True when the reviewer picked `departmentId` this session. Only then is
      `department_id` sent: under the absent-vs-null convention an unpicked
      value would either clear a derived department (null) or pin it as
      `Manual` over the one the server re-derives for a new account. */
  departmentPicked?: boolean
  // NO account PROVENANCE and no printed-code field. Both were read only by
  // the per-line "how was this account chosen" popover, which issue 17
  // removed, and neither is ever sent — `InvoiceLineItemWrite` has no source
  // column, so the server stamps provenance itself.
  // NO PER-LINE COMPANY (owner item 5, 2026-08-17; code review B-05,
  // 2026-10-03). An invoice has one company, stored on the invoice; a line
  // has none, and the server refuses one sent on a line.
  /** True for a line the reviewer added manually and hasn't saved yet — created
      (not updated) on save, then flipped false once it has a real id. */
  isNew?: boolean
  // NO `combines` SINCE D-396 (2026-10-22): Combine makes a group, held in the
  // editor's `groups`, and its lines stay.
}

/**
 * ONE GROUP AS THE EDITOR HOLDS IT (owner, 2026-10-22 — D-396, D-399): its
 * name and the rows under it. A group row stores no value of its own (G-6), so
 * this is all there is to it on screen; its Account, Department, Marketing
 * Category and total are read from its rows (`groupFieldViews`).
 */
export interface GroupState {
  /** Stable on screen: the stored group row's id, or `group-new-N` until the save that creates it. */
  key: string
  /** The stored group row's id, once saved. */
  storedId?: string
  /** Its description. */
  name: string
  /** The group row's own line number, once saved. */
  sequence?: number | null
  /** The rows under it, by `lineItemId`. */
  members: readonly string[]
  /**
   * THE INVOICE'S TAX OR SHIPPING GROUP (owner, 2026-10-27 — D-420): its kind.
   * Absent on every other group. Its name is fixed, it is never removed or
   * renamed, it stays when empty, and its lines carry no Account, Department
   * or Marketing Category.
   */
  chargeKind?: ChargeKind
}

/** The two charge groups, in the order the table shows them: Tax, then Shipping (D-420). */
export const CHARGE_KINDS: readonly ChargeKind[] = [CHARGE_KIND.tax, CHARGE_KIND.shipping]

/**
 * The key of a charge group the invoice does not hold yet (D-420): the table
 * draws a placeholder under it, and the editor makes the group under the same
 * key when a line is moved there, so the row does not remount.
 */
export function chargePlaceholderKey(kind: ChargeKind): string {
  return `charge:${kind}`
}

/** The charge kind a placeholder key names, or null for any other key. */
export function chargeKindOfPlaceholder(key: string): ChargeKind | null {
  return CHARGE_KINDS.find((kind) => chargePlaceholderKey(kind) === key) ?? null
}

/** The rows in the invoice's charge groups (D-420): a charge line has no coding to require. */
export function chargeMemberIds(groups: readonly GroupState[]): Set<string> {
  return new Set(groups.filter((g) => g.chargeKind).flatMap((g) => g.members))
}

/** The groups the stored lines hold: each active group row and its active items. */
export function groupsFrom(lineItems: readonly InvoiceLineItem[]): GroupState[] {
  return lineItems
    .filter((c) => c.is_active && isGroupRow(c))
    .map((g) => ({
      key: g.id,
      storedId: g.id,
      // A charge group's name is fixed (D-420).
      name: g.charge_kind ? CHARGE_GROUP_NAME[g.charge_kind] : (g.description ?? ''),
      sequence: g.line_number,
      members: realLines(lineItems)
        .filter((c) => c.group_id === g.id)
        .sort((a, b) => a.line_number - b.line_number)
        .map((c) => c.id),
      ...(g.charge_kind ? { chargeKind: g.charge_kind } : {}),
    }))
}

/**
 * THE GROUP CHANGES A SAVE SENDS (D-399): the difference between the groups on
 * screen and the groups as last stored, as the operations the server applies
 * in order — ungroups, removals, new groups, moves, renames. A line names
 * itself by id, or by the line number a line added in this same save asks for.
 * The server rewrites every group row and dissolves a group of one (G-6, G-21).
 */
export function groupOperations(
  groups: readonly GroupState[],
  baseline: readonly GroupState[],
  rows: readonly LineItemState[],
): LineGroupOperation[] {
  const byId = new Map(rows.map((r) => [r.lineItemId, r]))
  const ref = (id: string) => {
    const row = byId.get(id)
    return row?.isNew ? { line_number: row.sequence as number } : { id }
  }
  const live = (id: string) => byId.has(id)
  const grouped = new Set(groups.flatMap((g) => g.members))
  const kept = new Map(groups.filter((g) => g.storedId).map((g) => [g.storedId as string, g]))
  const ops: LineGroupOperation[] = []
  for (const was of baseline) {
    if (was.storedId && !kept.has(was.storedId)) ops.push({ op: 'ungroup', group_id: was.storedId })
  }
  for (const was of baseline) {
    const now = was.storedId ? kept.get(was.storedId) : undefined
    if (!now) continue
    const left = was.members.filter((id) => !now.members.includes(id) && !grouped.has(id) && live(id))
    if (left.length > 0) ops.push({ op: 'remove', lines: left.map(ref) })
  }
  for (const g of groups) {
    if (g.storedId) continue
    const members = g.members.filter(live)
    // A CHARGE GROUP NOT STORED YET (D-420) is made by the move itself: the
    // server makes the invoice's Tax or Shipping group when it has none.
    if (g.chargeKind) {
      if (members.length > 0) ops.push({ op: 'move', charge_kind: g.chargeKind, lines: members.map(ref) })
      continue
    }
    if (members.length >= 1) ops.push({ op: 'create', name: g.name, lines: members.map(ref) })
  }
  for (const g of groups) {
    if (!g.storedId) continue
    const was = baseline.find((b) => b.storedId === g.storedId)
    const joined = g.members.filter((id) => live(id) && !(was?.members ?? []).includes(id))
    if (joined.length > 0) {
      ops.push(
        g.chargeKind
          ? { op: 'move', charge_kind: g.chargeKind, lines: joined.map(ref) }
          : { op: 'move', group_id: g.storedId, lines: joined.map(ref) },
      )
    }
    // A charge group's name is fixed: never renamed (D-420).
    if (!g.chargeKind && was && was.name !== g.name && g.name.trim() !== '') ops.push({ op: 'rename', group_id: g.storedId, name: g.name })
  }
  return ops
}

/** The editor's header state for a stored invoice. The Company is the
    invoice's own `company_id` — the one field every screen reads (code review
    D-03, 2026-10-03). */
export function headerStateFrom(invoice: Invoice): HeaderState {
  return {
    // THREE FIELDS CARRY A DEFAULT WHEN INTAKE FOUND NOTHING (owner,
    // 2026-09-21 — D-128). THE SERVER OWNS THEM: the intake save fills an empty
    // field itself and does not count the fill as a correction (code review
    // E-03, 2026-10-03). They are shown here too, so the box the reviewer sees
    // holds what the save will store. `MissingExternalInvoiceId` still fires at
    // intake: the flag says the document printed no number, and the default is
    // there so nobody has to key one by hand.
    //
    // The date is used in its STORED form (`YYYY-MM-DD`), not the displayed
    // one: this value reaches the Point Click Care export and half the
    // duplicate key, and neither should move with a locale.
    externalinvoiceid: invoice.external_invoice_id ?? (invoice.invoice_date ?? '').slice(0, 10),
    ...(invoice.external_invoice_id == null
      ? { externalInvoiceIdDefault: (invoice.invoice_date ?? '').slice(0, 10) }
      : {}),
    date: invoice.invoice_date ? invoice.invoice_date.slice(0, 10) : '',
    subtotal: invoice.subtotal != null ? money2(String(invoice.subtotal)) : '',
    // Tax and Shipping mean ZERO when the document shows neither, and an empty
    // box would otherwise block the release as a missing required field.
    tax: invoice.tax != null ? money2(String(invoice.tax)) : '0.00',
    shipping: invoice.shipping != null ? money2(String(invoice.shipping)) : '0.00',
    total: invoice.total != null ? money2(String(invoice.total)) : '',
    currency: invoice.currency ?? 'USD',
    vendorId: invoice.vendor_id || undefined,
    vendorName: invoice.vendor_name || undefined,
    companyId: invoice.company_id || undefined,
    companyName: invoice.company_name || undefined,
    manualEntry: invoice.manual_entry_requested ?? false,
  }
}

/** The editor's row state for a stored line: the seed, and the reseed after a
    save. */
export function lineItemStateFrom(c: InvoiceLineItem): LineItemState {
  const price = priceFromWire(c.unit_price)
  const quantity = c.quantity != null ? String(c.quantity) : ''
  const total = c.total != null ? money2(String(c.total)) : ''
  return {
    lineItemId: c.id,
    rowVersion: c.row_version,
    sequence: c.line_number,
    description: c.description ?? '',
    quantity,
    // EXACT, every decimal kept (D-249): $0.0049 opens as 0.0049, not 0.00.
    price,
    // THE STORED TOTAL, as the server derived it (code review D-02). The
    // editor re-derived every Total on open, so a client rule that drifted
    // from the server's showed a "doesn't reconcile" no flag stood behind
    // (D-249). It derives only on an edit, with the server's own rule.
    total,
    stored: { price, quantity, total },
    accountId: c.account_id || undefined,
    // The stored department, re-derived by the server after an account
    // change; a fresh read is never the reviewer's own pick (D-217).
    departmentId: c.department_id || undefined,
  }
}

/**
 * A ROW'S TOTAL for the Price and Quantity it now holds (code review D-02). The
 * stored Total while both equal the stored ones BY VALUE — touching a box, or
 * typing `6066.0000` over `6066`, changes nothing — and `lineTotal` only once
 * one of them really differs. A line added this session has no stored Total.
 */
export function rowTotal(row: Pick<LineItemState, 'stored'>, price: string, quantity: string): string {
  const s = row.stored
  if (s && sameDecimal(price, s.price) && sameDecimal(quantity, s.quantity)) return s.total
  return lineTotal(price, quantity)
}

/**
 * The last-synced write signature of each stored line, keyed by its id — what
 * `changedLineIds` compares the rows against to decide which a save sends.
 * Seeded from the LOADED lines, so the first save compares against what the
 * server already has rather than counting every row as changed.
 */
export function lineChangeBaselineFrom(lineItems: readonly InvoiceLineItem[]): Map<string, string> {
  return new Map(
    lineItems.map((c) => [
      c.id,
      lineWriteSignature({
        lineItemId: c.id,
        sequence: c.line_number,
        description: c.description ?? '',
        accountId: c.account_id || undefined,
        quantity: c.quantity != null ? String(c.quantity) : '',
        price: priceFromWire(c.unit_price),
      }),
    ]),
  )
}

/** A row's key on screen and in the focus keys (`description:<key>`): stable
    across the save that gives a new line its id. */
export function rowKey(r: Pick<LineItemState, 'lineItemId' | 'clientKey'>): string {
  return r.clientKey ?? r.lineItemId
}

interface IntakeSaveBodyInput {
  header: HeaderState
  rows: readonly LineItemState[]
  /** The rows whose write signature moved (`changedLineIds`); only these are sent. */
  changedIds: ReadonlySet<string>
  rowVersion: number
  /** The line's current counter, when the caller holds a fresher one than the row state. */
  lineRowVersion: (lineItemId: string) => number | undefined
  removed: readonly string[]
  release: boolean
  /** The Override pop-up's answer, `null` to clear it; absent leaves the
      stored Override alone (D-350). */
  routeOverride?: RouteOverrideWrite | null
  /** The groups on screen and as last stored (D-399); absent sends no group change. */
  groups?: { now: readonly GroupState[]; stored: readonly GroupState[] }
}

/** Whether the External Invoice ID box holds something the reviewer typed,
    rather than the default it was seeded with (or the current date's default). */
export function externalIdTyped(header: HeaderState): boolean {
  if (header.externalInvoiceIdDefault === undefined) return true
  const v = header.externalinvoiceid.trim()
  return v !== '' && v !== header.externalInvoiceIdDefault && v !== header.date.trim()
}

/** The intake-save body the editor sends for `input`. */
export function buildIntakeSaveBody(input: IntakeSaveBodyInput): IntakeSaveRequest {
  const { header } = input
  // A blank field CLEARS the stored column: the request is PATCH-shaped, where
  // an absent key means "leave alone" and null means "clear".
  const textOrNull = (v: string): string | null => (v.trim() !== '' ? v.trim() : null)
  // MONEY IS A DECIMAL STRING ON THE WIRE (docs/api.md's global conventions):
  // the server's `field.money` refuses a JSON number outright.
  const decimalOrNull = (v: string): string | null => (v.trim() !== '' ? money2(v) : null)
  // A QUANTITY IS NOT MONEY (code review E-03): `numeric(14,4)`, so `money2`
  // turned 0.125 into 0.13 and changed the line's total. Sent at four places
  // (`quantityWire`), and the server compares it by value.
  const quantityOrNull = (v: string): string | null => quantityWire(v)

  const changed = input.rows.filter((r) => input.changedIds.has(r.lineItemId))
  const groupOps = input.groups ? groupOperations(input.groups.now, input.groups.stored, input.rows) : []
  // No id = create. With an id the line's own concurrency counter rides along,
  // so an edit to a line someone else just changed is refused rather than
  // silently winning.
  const identity = (r: LineItemState): Pick<InvoiceLineItemWrite, 'id' | 'row_version'> =>
    r.isNew ? {} : { id: r.lineItemId, row_version: input.lineRowVersion(r.lineItemId) ?? r.rowVersion }
  const upsert: InvoiceLineItemWrite[] = changed.map((r) => ({
    ...identity(r),
    // Every saved line carries its number, and a new one is numbered when added.
    line_number: r.sequence as number,
    description: textOrNull(r.description),
    account_id: r.accountId ?? null,
    // THE DEPARTMENT ONLY WHEN PICKED (D-217). Absent otherwise: sending the
    // stored value would be ignored, but sending null would CLEAR a
    // `ChartImplied` department on every save.
    ...(r.departmentPicked ? { department_id: r.departmentId ?? null } : {}),
    quantity: quantityOrNull(r.quantity),
    // The price exactly as it stands (D-249) — never through `money2`.
    unit_price: r.price.trim() !== '' ? priceText(r.price) : null,
    // NO `total`: the server derives every line total from price x quantity
    // (D-249) and ignored the one sent (code review D-07).
  }))
  return {
    row_version: input.rowVersion,
    invoice: {
      // ONLY WHEN TYPED (code review, 2026-10-03). An empty External Invoice ID
      // takes the invoice date on the server (D-128), from the date this save
      // leaves in place; sending the seeded default would pin the OLD date when
      // the reviewer corrects the date in the same save.
      ...(externalIdTyped(header) ? { external_invoice_id: textOrNull(header.externalinvoiceid) } : {}),
      invoice_date: textOrNull(header.date),
      // NO `subtotal` (the server derives it from the saved lines, D-3), NO
      // `currency` (no control, D-8; absent leaves it alone) and NO
      // `manual_entry_requested` (no control on this form) — code review D-07.
      tax: decimalOrNull(header.tax),
      shipping: decimalOrNull(header.shipping),
      total: decimalOrNull(header.total),
      vendor_id: header.vendorId ?? null,
      // The reviewer's Company — changeable here, the one step that settles it.
      // The ids only: the server reads both NAMES from the masters, so the app
      // sends neither (code review F-08, 2026-10-03).
      company_id: header.companyId ?? null,
    },
    line_items: { upsert, remove: [...input.removed] },
    // THE GROUP CHANGES (D-399), applied by the server after the lines above.
    ...(groupOps.length > 0 ? { line_groups: groupOps } : {}),
    release: input.release,
    ...(input.routeOverride === undefined ? {} : { route_override: input.routeOverride }),
  }
}
